import React from 'react';
import { motion } from 'framer-motion';
export interface SpeedDiagramProps {
  /** Speed value (km/h or m/s) */
  speed?: number;
  /** Distance value */
  distance?: number;
  /** Time value in hours */
  time?: number;
  /** Unit for speed */
  speedUnit?: string;
  /** Unit for distance */
  distanceUnit?: string;
  /** Unit for time */
  timeUnit?: string;
  /** Which value to highlight/hide (for stimulus mode) */
  unknown?: 'speed' | 'distance' | 'time';
  /** Label text */
  label?: string;
  /** Display mode */
  mode?: 'stimulus' | 'support';
  /** How to format the time value */
  timeFormat?: 'decimal' | 'hours-minutes';
}
function formatTime(hours: number, format: 'decimal' | 'hours-minutes'): string {
  if (format === 'decimal') {
    return new Intl.NumberFormat('nl-NL', {
      maximumFractionDigits: 2
    }).format(hours) + ' u';
  }
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h > 0 && m > 0) return `${h} u ${m} min`;
  if (h > 0) return `${h} u`;
  return `${m} min`;
}
function formatValue(value: number | undefined, unit: string, isTime: boolean, timeFormat: 'decimal' | 'hours-minutes'): string {
  if (value === undefined) return '?';
  if (isTime) return formatTime(value, timeFormat);
  const formatted = new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(value);
  return `${formatted} ${unit}`;
}
export function SpeedDiagram({
  speed,
  distance,
  time,
  speedUnit = 'km/u',
  distanceUnit = 'km',
  timeUnit = 'uur',
  unknown,
  label,
  mode = 'support',
  timeFormat = 'hours-minutes'
}: SpeedDiagramProps) {
  const isStimulus = mode === 'stimulus';
  const showSpeed = !(isStimulus && unknown === 'speed');
  const showDistance = !(isStimulus && unknown === 'distance');
  const showTime = !(isStimulus && unknown === 'time');
  const speedDisplay = showSpeed ? formatValue(speed, speedUnit, false, timeFormat) : '?';
  const timeDisplay = showTime ? formatValue(time, timeUnit, true, timeFormat) : '?';
  const distanceDisplay = showDistance ? formatValue(distance, distanceUnit, false, timeFormat) : '?';
  const getCellClass = (type: 'speed' | 'distance' | 'time') => {
    const base = 'text-center font-bold px-3 py-2 rounded-lg transition-colors';
    if (unknown === type) {
      return `${base} ring-2 ring-amber-400 bg-amber-50 text-amber-900`;
    }
    return `${base} text-slate-800`;
  };
  return <div className={`w-full max-w-md mx-auto ${mode === 'support' ? 'bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm' : ''}`} data-id="element-12556">
      {label && <p className={`text-sm font-semibold text-slate-700 mb-4 text-center ${mode === 'support' ? 'text-base' : ''}`} data-id="element-12557">
          {label}
        </p>}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-12558">
        {/* Header Row */}
        <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center bg-slate-100 border-b border-slate-200 p-2" data-id="element-12559">
          <div className="text-center font-semibold text-slate-600 text-xs uppercase tracking-wider" data-id="element-12560">
            Snelheid
          </div>
          <div className="w-6" data-id="element-12561"></div>
          <div className="text-center font-semibold text-slate-600 text-xs uppercase tracking-wider" data-id="element-12562">
            Tijd
          </div>
          <div className="w-6" data-id="element-12563"></div>
          <div className="text-center font-semibold text-slate-600 text-xs uppercase tracking-wider" data-id="element-12564">
            Afstand
          </div>
        </div>

        {/* Value Row */}
        <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center p-3 gap-y-2" data-id="element-12565">
          <div className={getCellClass('speed')} data-id="element-12566">{speedDisplay}</div>
          <div className="text-slate-400 font-bold text-lg text-center" data-id="element-12567">×</div>
          <div className={getCellClass('time')} data-id="element-12568">{timeDisplay}</div>
          <div className="text-slate-400 font-bold text-lg text-center" data-id="element-12569">=</div>
          <div className={getCellClass('distance')} data-id="element-12570">{distanceDisplay}</div>
        </div>
      </div>

      {/* Support Mode Calculation Strip */}
      {mode === 'support' && unknown && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mt-4 bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-id="element-12571">
          <div className="flex items-start gap-3" data-id="element-12572">
            <span className="text-xl" data-id="element-12573">💡</span>
            <div className="text-sm text-slate-700 space-y-1" data-id="element-12574">
              <p className="font-semibold text-slate-800" data-id="element-12575">Berekening:</p>
              {unknown === 'distance' && speed !== undefined && time !== undefined && <p data-id="element-12576">
                    {formatValue(speed, speedUnit, false, timeFormat)} ×{' '}
                    {formatValue(time, timeUnit, true, timeFormat)} ={' '}
                    <strong className="text-slate-900" data-id="element-12577">
                      {formatValue(distance, distanceUnit, false, timeFormat)}
                    </strong>
                  </p>}
              {unknown === 'speed' && distance !== undefined && time !== undefined && <p data-id="element-12578">
                    {formatValue(distance, distanceUnit, false, timeFormat)} ÷{' '}
                    {formatValue(time, timeUnit, true, timeFormat)} ={' '}
                    <strong className="text-slate-900" data-id="element-12579">
                      {formatValue(speed, speedUnit, false, timeFormat)}
                    </strong>
                  </p>}
              {unknown === 'time' && distance !== undefined && speed !== undefined && <p data-id="element-12580">
                    {formatValue(distance, distanceUnit, false, timeFormat)} ÷{' '}
                    {formatValue(speed, speedUnit, false, timeFormat)} ={' '}
                    <strong className="text-slate-900" data-id="element-12581">
                      {formatValue(time, timeUnit, true, timeFormat)}
                    </strong>
                  </p>}
            </div>
          </div>
        </motion.div>}
    </div>;
}